import i18next, { use, t, changeLanguage } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { Resource, ResourceLanguage, TFunction as i18nTFunction } from 'i18next';
import type { LocalStore } from 'zf-web-infra';
import type { I18nData, Locale } from './types';

/**
 * 语言包初始化
 * 为了方便维护，词条的格式是设计为 key: { en: 'xxx', jp: 'xxx', zh_cn: 'xxx', zh_hk: 'xxx' } 样子
 * 这里将其转换为 i18next 的格式，并初始化i18next实例
 */
class I18N {

  private static readonly NS = 'translation';

  init(local: LocalStore, data: I18nData): void {
    if (i18next.isInitialized) {
      this.append(data);
      return;
    }

    use(initReactI18next).init({
      returnNull: false,
      resources: this.formatLang(data),
      lng: this.lang(local),
      nsSeparator: false,
      defaultNS: I18N.NS
    });
  }

  /**
   * 这个方法用于后期动态添加语言包
   * 定制逻辑里添加的语言包无法在初始化是加载，所以提供一个动态添加的方法
   * @param data
   */
  append(data: I18nData): void {
    Object.keys(data).forEach(key => {
      for (const lang of Object.keys(data[key]) as Locale[]) {
        i18next.addResource(lang, I18N.NS, key, data[key][lang]);
      }
    });
  }

  private lang(local: LocalStore): Locale {
    return local.lang as Locale ?? 'zh_cn';
  }

  private formatLang(data: I18nData): Resource {
    const initial: Resource = {
      en: { translation: {} },
      jp: { translation: {} },
      zh_cn: { translation: {} },
      zh_hk: { translation: {} }
    };

    return Object.keys(data).reduce((memo, key) => {
      for (const lang of Object.keys(data[key]) as Locale[]) {
        (memo[lang].translation as ResourceLanguage)[key] = data[key][lang];
      }
      return memo;
    }, initial);
  }

}

export const i18n = new I18N();

export {
  t,
  useTranslation,
  changeLanguage
};

export type TFunction = i18nTFunction;
